import { todayWithSystemTimeZone } from '@magicdoorinc/date';
import { useLocalization, toast, createLazyResource } from '@magicdoorinc/ui';
import { createEffect, createMemo, createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { clearChatInput, clearPendingChatInput, migratePendingToChat } from '~/pages/chats/chat-details/components/chatInputStore';
import {
  getDefaultSendMethod,
  getSendMethodForChat,
  migratePendingSendMethod,
  setSendMethodForChat,
} from '~/pages/chats/chat-details/components/chatSendMethodStore';
import { ChatRepository } from '~/repositories/chatRepository';
import { phonesRepository } from '~/repositories/phones';
import { ChatNoteKind, ChatTimelineItemKind, MessageType } from '~/swagger/portal/Api';
import { useChat } from './ChatContext';
import type { ChatSendMethod } from '~/pages/chats/chat-details/components/chatSendMethodStore';
import type { SendChatData } from '~/repositories/chatRepository';
import type {
  ChatMessageDto,
  ChatNoteDto,
  ChatTimelineItemDto,
  ChatTypes,
  EntityType,
  MagicChatTextResponseDto,
  MessageSource,
  PropertyManagerDto,
} from '~/swagger/portal/Api';

export const [ChatMessageProvider, useChatMessage] = createMagicDoorContext('ChatMessage', () => {
  const { t } = useLocalization();
  const repo = new ChatRepository();
  const [searchMessages, setSearchMessages] = createSignal<ChatMessageDto[]>([]);
  const [chatMessages, setChatMessages] = createSignal<ChatMessageDto[]>([]);
  const { createChat, initializeChat, setIsLoading: setIsChatLoading, setError: setChatError, isSearchMode, chatId } = useChat();
  const [isSendingMessage, setIsSendingMessage] = createSignal<boolean>(false);
  const [files, setFiles] = createSignal<File[]>([]);
  const defaultSendMethod = getDefaultSendMethod();
  const [sendEmail, setSendEmail] = createSignal(defaultSendMethod.sendEmail);
  const [sendText, setSendText] = createSignal(defaultSendMethod.sendText);
  const [sendMethodScope, setSendMethodScope] = createSignal<{
    chatId?: string | null;
    participantType?: string;
    participantId?: string | null;
  }>({});
  const [isAfterLoading, setIsAfterLoading] = createSignal<boolean>(false);
  const [requestFocus, setRequestFocus] = createSignal<boolean>(false);
  const [aiMessage, setAiMessage] = createSignal<string>('');
  const [newMessage, setNewMessage] = createSignal<string>('');
  const [hasMoreAfterMessages, setHasMoreAfterMessages] = createSignal<boolean>(false);
  const [hasMoreMessages, setHasMoreMessages] = createSignal<boolean>(true);
  const [jumpToMessage, setJumpToMessage] = createSignal<ChatMessageDto | null>(null);
  const [hasLoadedSearchAfter, setHasLoadedSearchAfter] = createSignal(false);
  const [latestMessage, setLatestMessage] = createSignal<ChatMessageDto | null>(null);
  const [canCompanySendTextMessages] = createLazyResource(() => phonesRepository.canCompanySendTextMessages());

  const [chatNotes, setChatNotes] = createSignal<ChatNoteDto[]>([]);
  const [notePropertyManagers, setNotePropertyManagers] = createSignal<Record<string, PropertyManagerDto>>({});
  const [noteCreatedTick, setNoteCreatedTick] = createSignal<number>(0);

  const messageSetters: Record<'chat' | 'search', typeof setChatMessages> = {
    chat: setChatMessages,
    search: setSearchMessages,
  };

  const feedTimestamp = (item: ChatTimelineItemDto): number => {
    const iso = item.kind === ChatTimelineItemKind.Message ? item.message?.sentAt : item.note?.createdAt;
    const time = iso ? Date.parse(iso) : NaN;
    return Number.isNaN(time) ? 0 : time;
  };

  const feedItems = createMemo<ChatTimelineItemDto[]>(() => {
    const messageItems: ChatTimelineItemDto[] = chatMessages().map((message) => ({
      id: message.id,
      kind: ChatTimelineItemKind.Message,
      message,
    }));
    const noteItems: ChatTimelineItemDto[] = chatNotes().map((note) => ({
      id: note.id,
      kind: note.kind === ChatNoteKind.Action ? ChatTimelineItemKind.Action : ChatTimelineItemKind.Note,
      note,
    }));

    return [...messageItems, ...noteItems].sort((a, b) => {
      const timeA = feedTimestamp(a);
      const timeB = feedTimestamp(b);
      if (timeA !== timeB) return timeB - timeA;
      return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
    });
  });

  const mergePropertyManagers = (managers: PropertyManagerDto[]) => {
    if (!managers.length) return;
    setNotePropertyManagers((prev) => {
      const next = { ...prev };
      managers.forEach((manager) => (next[manager.id] = manager));
      return next;
    });
  };

  const upsertNote = (note: ChatNoteDto) => {
    if (note.chatId !== chatId()) return;
    setChatNotes((prev) => {
      const without = prev.filter((existing) => existing.id !== note.id);
      return [note, ...without];
    });
  };

  const removeNote = (noteId: string) => {
    setChatNotes((prev) => prev.filter((note) => note.id !== noteId));
  };

  const createNote = async (targetChatId: string, body: string): Promise<ChatNoteDto | undefined> => {
    try {
      const note = await repo.createChatNote(targetChatId, body);
      upsertNote(note);
      setNoteCreatedTick((tick) => tick + 1);
      return note;
    } catch (err) {
      setChatError(err instanceof Error ? err : new Error(String(err)));
      toast.error(t('Operation failed, please try again later'));
    }
  };

  const editNote = async (targetChatId: string, noteId: string, body: string): Promise<ChatNoteDto | undefined> => {
    try {
      const note = await repo.updateChatNote(targetChatId, noteId, body);
      upsertNote(note);
      return note;
    } catch (err) {
      setChatError(err instanceof Error ? err : new Error(String(err)));
      toast.error(t('Operation failed, please try again later'));
    }
  };

  const deleteNote = async (targetChatId: string, noteId: string): Promise<void> => {
    try {
      await repo.deleteChatNote(targetChatId, noteId);
      removeNote(noteId);
    } catch (err) {
      setChatError(err instanceof Error ? err : new Error(String(err)));
      toast.error(t('Operation failed, please try again later'));
    }
  };

  const rewriteMessage = async (chatId: string, messageId: string): Promise<MagicChatTextResponseDto | undefined> => {
    setIsChatLoading(true);
    try {
      const message = await repo.paraphraseMessage(chatId, messageId);
      return message;
    } catch (err) {
      setChatError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsChatLoading(false);
    }
  };

  const persistSendMethod = (method: ChatSendMethod) => {
    const scope = sendMethodScope();
    setSendMethodForChat(method, scope.chatId, scope.participantType, scope.participantId);
  };

  const hydrateSendMethod = (chatId?: string | null, participantType?: string, participantId?: string | null) => {
    setSendMethodScope({ chatId, participantType, participantId });
    const method = getSendMethodForChat(chatId, participantType, participantId);
    setSendEmail(method.sendEmail);
    setSendText(method.sendText);
  };

  const handleSetSendEmail = (value: boolean) => {
    setSendEmail(value);
    persistSendMethod({ sendEmail: value, sendText: sendText() });
  };

  const handleSetSendText = (value: boolean) => {
    setSendText(value);
    persistSendMethod({ sendEmail: sendEmail(), sendText: value });
  };

  const addNewMessage = (newMessage: any) => {
    setChatMessages((prevMessages) => [newMessage, ...prevMessages]);
  };

  const removeMessage = (messageId: string) => {
    setChatMessages((prevMessages) => prevMessages.filter((message) => message.id !== messageId));
  };

  const fetchChatMessagesById = async (chatId1: string, before?: string, after?: string, take = 20, search?: string, fileName?: string) => {
    return getChatMessageById('chat', chatId1, before, after, take, search, fileName);
  };

  const initChatMessage = async (chatId: string) => {
    const messages = await fetchChatMessagesById(chatId);
    setChatMessages(messages || []);
  };

  const fetchSearchMessagesById = async (
    chatId1: string,
    before?: string,
    after?: string,
    take = 20,
    search?: string,
    fileName?: string
  ) => {
    return getChatMessageById('search', chatId1, before, after, take, search, fileName);
  };

  const sendMessage = async (
    messageData: {
      messageContent: string;
      chatId: string;
      participantType?: `${EntityType}` | `${ChatTypes}`;
      participantId?: string | null;
    },
    scrollToBottom: () => void
  ) => {
    const { messageContent, chatId, participantType, participantId } = messageData;
    if (!messageContent.trim().length && !files().length) {
      toast.error(t('Cannot send empty messages'));
      return;
    }
    if (!sendEmail() && !sendText()) {
      toast.info(t('You have not selected an external message delivery method. This message will only be sent within the app.'));
    }
    setIsSendingMessage(true);

    let targetChatId = chatId;

    if (!targetChatId && participantType && participantId) {
      try {
        const newChatResult = await createChat(participantType, participantId);
        if (newChatResult && newChatResult.chatId) {
          targetChatId = newChatResult.chatId;
          migratePendingToChat(participantType, participantId, targetChatId);
          migratePendingSendMethod(participantType, participantId, targetChatId);
          hydrateSendMethod(targetChatId, participantType, participantId);
          await initializeChat(targetChatId, initChatMessage);
        } else {
          console.error('Failed to create a new chat: No chatId returned');
          setIsSendingMessage(false);
          return;
        }
      } catch (error) {
        console.error('Error creating chat:', error);
        setIsSendingMessage(false);
        return;
      }
    }

    if (targetChatId) {
      let optimisticMessageId: string | undefined;
      let hasCalledSendChat = false;

      try {
        const optimisticMessage: Partial<ChatMessageDto> = {
          id: `temp-${Date.now()}`,
          message: messageContent,
          sentAt: todayWithSystemTimeZone().toISOString(),
          chatId: targetChatId,
          messageType: MessageType.PropertyManager,
          sendEmail: sendEmail(),
          sendText: sendText(),
          source: 'app' as MessageSource,
        };
        optimisticMessageId = optimisticMessage.id;
        addNewMessage(optimisticMessage as ChatMessageDto);
        scrollToBottom();

        const currentFiles = files();
        const uploadSessionIds = currentFiles.length ? await repo.uploadChatMessageFiles(targetChatId, currentFiles) : undefined;

        hasCalledSendChat = true;
        const result = await sendChat(targetChatId, {
          message: messageContent,
          sendEmail: sendEmail(),
          sendText: sendText(),
          uploadSessionIds,
        });

        if (!result?.status) {
          removeMessage(optimisticMessage.id as string);
        }

        setFiles([]);
        setNewMessage('');
        setAiMessage('');

        if (targetChatId) {
          clearChatInput(targetChatId);
        }

        if (!chatId && participantType && participantId) {
          clearPendingChatInput(participantType, participantId);
        } else if (chatId && chatId !== targetChatId) {
          clearChatInput(chatId);
        }
      } catch (error) {
        if (optimisticMessageId) {
          removeMessage(optimisticMessageId);
        }

        if (!hasCalledSendChat) {
          setChatError(error instanceof Error ? error : new Error(String(error)));
          toast.error(t('Operation failed, please try again later'));
        }

        console.error('Error sending message:', error);
      } finally {
        setIsSendingMessage(false);
      }
    } else {
      setIsSendingMessage(false);
      console.error('No chatId available to send message');
    }
    setRequestFocus(true);
  };

  const sendChat = async (chatId: string, chatData: SendChatData, retryCount = 3): Promise<Response | void> => {
    setIsChatLoading(true);
    try {
      const result = await repo.sendChatMessage(chatId, chatData);
      await fetchChatMessagesById(chatId);
      return result;
    } catch (err) {
      if (retryCount > 0) {
        return await sendChat(chatId, chatData, retryCount - 1);
      } else {
        setChatError(err instanceof Error ? err : new Error(String(err)));
        toast.error(t('Operation failed, please try again later'));
      }
    } finally {
      setIsChatLoading(false);
    }
  };

  const chatMagicIt = async (chatId: string, chatData: string): Promise<MagicChatTextResponseDto | undefined> => {
    setIsChatLoading(true);

    try {
      const response = await repo.sendChatMagicIt(chatId, chatData);
      await fetchChatMessagesById(chatId);
      return response;
    } catch (err) {
      setChatError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsChatLoading(false);
    }
  };

  const getChatMessageById = async (
    type: 'chat' | 'search',
    chatId1: string,
    before?: string,
    after?: string,
    take = 20,
    search?: string,
    fileName?: string
  ) => {
    if (!chatId) return;

    const isSameChatNewSearch = latestMessage() ? after !== latestMessage()!.sentAt : false;

    if (isSearchMode() && (chatId1 !== chatId() || isSameChatNewSearch)) {
      setHasLoadedSearchAfter(false);
      setLatestMessage(null);
    }

    try {
      isSearchMode() ? setIsAfterLoading(true) : setIsChatLoading(true);
      const page = await repo.getChatMessages(chatId1, before, after, take, search, fileName);
      const data = page.messages;

      if (type === 'chat') {
        mergePropertyManagers(page.propertyManagers);
        if (!before && !after) {
          setChatNotes(page.notes);
        } else if (page.notes.length) {
          setChatNotes((prev) => {
            const seen = new Set(page.notes.map((note) => note.id));
            return [...page.notes, ...prev.filter((note) => !seen.has(note.id))];
          });
        }
      }

      if (data.length < take) {
        if (after) {
          setHasMoreAfterMessages(false);
          setHasMoreMessages(true);
        } else {
          setHasMoreMessages(false);
        }
      } else {
        after ? setHasMoreAfterMessages(true) : setHasMoreMessages(true);
      }

      if (before) {
        messageSetters[type]((prev) => [...prev, ...data]);
      } else if (after) {
        const reverseData = data.reverse();
        const finalData = (prev: ChatMessageDto[]) => {
          if (isSearchMode()) {
            setLatestMessage(data[0]);

            if (!hasLoadedSearchAfter()) {
              setHasLoadedSearchAfter(true);
              jumpToMessage() && reverseData.push(jumpToMessage() as ChatMessageDto);
              setJumpToMessage(null);
              return reverseData;
            }

            return [...reverseData, ...prev];
          } else {
            return [...reverseData, ...prev];
          }
        };
        messageSetters[type](finalData);
      } else {
        messageSetters[type](data);
      }

      return data;
    } catch (err) {
      setChatError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      isSearchMode() ? setIsAfterLoading(false) : setIsChatLoading(false);
    }
  };

  createEffect(() => {
    setChatMessages([]);
  });

  return {
    isSendingMessage,
    chatMessages,
    isSearchMode,
    files,
    setFiles,
    addNewMessage,
    setChatMessages,
    sendMessage,
    hasLoadedSearchAfter,
    hasMoreMessages,
    fetchChatMessagesById,
    isAfterLoading,
    hasMoreAfterMessages,
    searchMessages,
    canCompanySendTextMessages,
    sendEmail,
    sendText,
    setSendEmail: handleSetSendEmail,
    setSendText: handleSetSendText,
    hydrateSendMethod,
    newMessage,
    aiMessage,
    setNewMessage,
    setAiMessage,
    rewriteMessage,
    requestFocus,
    setRequestFocus,
    chatMagicIt,
    fetchSearchMessagesById,
    setJumpToMessage,
    initChatMessage,
    feedItems,
    chatNotes,
    notePropertyManagers,
    noteCreatedTick,
    upsertNote,
    removeNote,
    createNote,
    editNote,
    deleteNote,
  };
});
